/**
 * SessionTimeoutWarning Component
 *
 * Shows a warning dialog when the user has been inactive for too long.
 * Allows the user to extend their session or log out.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import { useTranslation } from 'react-i18next';
import { Clock } from 'lucide-react';

export function SessionTimeoutWarning() {
  const { showWarning, minutesRemaining, extendSession, logoutNow } = useSessionTimeout();
  const { t } = useTranslation('common');

  return (
    <AlertDialog open={showWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/30">
              <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            </div>
            <AlertDialogTitle>{t('sessionTimeout.title')}</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            {t('sessionTimeout.description', {
              count: minutesRemaining,
              unit:
                minutesRemaining === 1
                  ? t('sessionTimeout.minuteUnit')
                  : t('sessionTimeout.minutesUnit'),
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={logoutNow}>{t('sessionTimeout.logout')}</AlertDialogCancel>
          <AlertDialogAction onClick={extendSession}>
            {t('sessionTimeout.stayLoggedIn')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
